"use client";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "@/redux/store";
import { selectUser } from "@/redux/slices/authSlice";
import type { User } from "@/redux/slices/authSlice";
import { paymentAPI } from "@/lib/services/api.service";

/**
 * Shown when billing is not active but required (matches widget embed / public config gate).
 * Covers home, live, and the rest of the dashboard via DashboardLayout.
 */
export default function PaymentSetupBanner() {
  const user = useAppSelector(selectUser) as User | null;
  const [state, setState] = useState<{
    loading: boolean;
    show: boolean;
    reason?: string;
  }>({ loading: true, show: false });

  useEffect(() => {
    if (!user?.company_id || user.role === "SUPER_ADMIN") {
      setState({ loading: false, show: false });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const s = await paymentAPI.getPaymentStatus();
        if (cancelled) return;
        const show = Boolean(s && !s.isActive && s.requiresPayment);
        setState({
          loading: false,
          show,
          reason: s.paymentRequiredReason,
        });
      } catch {
        if (!cancelled) setState({ loading: false, show: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.company_id, user?.role]);

  if (state.loading || !state.show) {
    return null;
  }

  const isAdmin = user?.role === "ADMIN";

  return (
    <div
      className="mb-4 rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm text-amber-950 dark:text-amber-100 dark:border-amber-400/30 dark:bg-amber-500/15"
      role="status"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="font-semibold text-amber-950 dark:text-amber-50">
            Billing required for the live widget
          </p>
          <p className="mt-1 text-amber-900/90 dark:text-amber-100/90">
            Visitors will not see the chat widget on your website until payment is set up. Dashboard
            previews still work.
            {state.reason ? (
              <span className="block mt-1 text-xs opacity-90">{state.reason}</span>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {isAdmin ? (
            <Link
              to="/payment"
              className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-amber-500 dark:bg-amber-500 dark:hover:bg-amber-400"
            >
              Add payment
            </Link>
          ) : (
            <p className="text-xs text-amber-900/85 dark:text-amber-100/85 max-w-[220px]">
              Ask a company admin to complete billing so the widget appears on your site.
            </p>
          )}
          <Link
            to="/billing"
            className="inline-flex items-center justify-center rounded-lg border border-amber-600/40 px-3 py-2 text-xs font-medium text-amber-950 hover:bg-amber-500/20 dark:text-amber-50 dark:border-amber-400/40"
          >
            Billing
          </Link>
        </div>
      </div>
    </div>
  );
}
